import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateCheckinDefinition } from "../../../../api/mutations";
import { useNotification } from "../../../../hooks/useNotification";
import Container from "../../../../componentLibrary/Container";
import Button from "../../../../componentLibrary/Button";
import type { ToolboxQuestionI } from "../../../../types";
import ReorderableVerticalList from "../../../../componentLibrary/ReorderableVerticalList";
import RbdFullRowDragSurface from "../../../../componentLibrary/RbdFullRowDragSurface";
import QuestionTypeList from "../ProfileDefinitions/components/QuestionTypeList";
import QuestionCard from "../ProfileDefinitions/components/questionCards/QuestionCard";
import {
  getNewQuestion,
  QuestionType,
} from "../ProfileDefinitions/utils/questionUtils";
import RatingConfig from "./components/RatingConfig";
import type { RatingConfigState } from "./components/RatingConfig";

export default function NewCheckinDefinitionPage() {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const [questions, setQuestions] = useState<ToolboxQuestionI[]>([]);
  const [ratingConfig, setRatingConfig] = useState<RatingConfigState>({
    ratingEnabled: true,
    ratingScale: 5,
    ratingLabel: "",
  });

  const backPath = `/admin/toolboxes/${topicId}/checkin-definitions`;

  const createMutation = useCreateCheckinDefinition({
    onSuccess: async () => {
      notify("success", "Check-in definition created");
      await queryClient.invalidateQueries({
        queryKey: ["toolboxCheckinDefinitions", topicId],
      });
      navigate(backPath);
    },
    onError: () => notify("error", "Failed to create check-in definition"),
  });

  const handleAdd = (type: QuestionType) =>
    setQuestions((prev) => [...prev, getNewQuestion(type)]);

  const handleChange = (updated: ToolboxQuestionI) =>
    setQuestions((prev) =>
      prev.map((q) => (q.questionId === updated.questionId ? updated : q)),
    );

  const handleRemove = (questionId: string) =>
    setQuestions((prev) => prev.filter((q) => q.questionId !== questionId));

  const handleSave = () => {
    if (!topicId) return;
    createMutation.mutate({
      topicId,
      questions,
      ratingEnabled: ratingConfig.ratingEnabled,
      ratingScale: ratingConfig.ratingEnabled
        ? ratingConfig.ratingScale
        : undefined,
      ratingLabel: ratingConfig.ratingEnabled
        ? ratingConfig.ratingLabel
        : undefined,
    });
  };

  return (
    <div className="pt-4">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Check-In Definitions
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            New version · {questions.length} question
            {questions.length !== 1 ? "s" : ""}
          </span>
          <Button
            type="button"
            size="sm"
            loading={createMutation.isPending}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 space-y-4 self-start">
          <Container>
            <QuestionTypeList onAdd={handleAdd} />
          </Container>
          <Container>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Rating
            </p>
            <RatingConfig config={ratingConfig} onChange={setRatingConfig} />
          </Container>
        </div>

        <div className="lg:col-span-9 space-y-4 pl-6">
          {questions.length === 0 ? (
            <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 py-20">
              <p className="text-sm text-gray-400">
                Add a question from the panel on the left.
              </p>
            </div>
          ) : (
            <ReorderableVerticalList
              items={questions}
              getId={(q) => q.questionId}
              onReorder={setQuestions}
              droppableId="toolbox-checkin-definition-questions-new"
              className="space-y-4"
            >
              {(q, i, provided) => (
                <RbdFullRowDragSurface provided={provided}>
                  <QuestionCard
                    question={q}
                    index={i}
                    onChange={handleChange}
                    onRemove={() => handleRemove(q.questionId)}
                  />
                </RbdFullRowDragSurface>
              )}
            </ReorderableVerticalList>
          )}
        </div>
      </div>
    </div>
  );
}
